/**
 * PositionBadgeView — position chip: plain when single, tappable
 * `${position} +N` when there are extra positions (SE-4141 behavior).
 * Promoted verbatim from MobileTargetCard's inline chip (SE-5527) — classes
 * unchanged so the card's layout and SSIM baselines are unaffected. Renders
 * nothing when the position is empty.
 * @module components/molecules/PositionBadge/PositionBadgeView
 */
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for PositionBadgeView. */
export interface PositionBadgeProps {
  /** Primary position abbreviation (e.g. "ST", "CAM"). */
  readonly position: string;
  /** Positions beyond the primary — renders `+N` and enables the tap. */
  readonly positionExtraCount: number;
  /** Tap handler for the multi-position badge. */
  readonly onPositionsPress: () => void;
  /** A11y label for the multi-position tap target. */
  readonly accessibilityLabel: string;
  /** Test identity for the multi-position tap target. */
  readonly testID?: string;
}

/** Generous tap target for the compact chip (hoisted — stable reference). */
const HIT_SLOP = { top: 8, bottom: 8, left: 4, right: 4 };

/**
 * Chip label — `${position} +N` when there are extra positions.
 * @param position - Primary position abbreviation
 * @param extraCount - Positions beyond the primary
 * @returns The chip label.
 */
const chipLabel = (position: string, extraCount: number): string =>
  extraCount > 0 ? `${position} +${extraCount}` : position;

/** The chip's visual classes — shared by the plain and tappable renderings. */
const CHIP_CLASS =
  "shrink-0 items-center justify-center rounded-sm bg-surface-raised px-2 py-1";

/**
 * Renders the position chip: null when empty, the plain chip when single,
 * a pressable chip when there are extra positions.
 * @param root0 - Position badge data + handler
 * @param root0.position - Primary position abbreviation
 * @param root0.positionExtraCount - Positions beyond the primary
 * @param root0.onPositionsPress - Tap handler for the multi-position badge
 * @param root0.accessibilityLabel - A11y label for the tap target
 * @param root0.testID - Test identity for the tap target
 * @returns The chip element, or null when the position is empty.
 */
const PositionBadgeView = ({
  position,
  positionExtraCount,
  onPositionsPress,
  accessibilityLabel,
  testID,
}: PositionBadgeProps) =>
  !position ? null : positionExtraCount <= 0 ? (
    <Box className={CHIP_CLASS}>
      <Text variant="caption" className="text-content-primary">
        {chipLabel(position, positionExtraCount)}
      </Text>
    </Box>
  ) : (
    <Pressable
      onPress={onPositionsPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      hitSlop={HIT_SLOP}
      testID={testID}
    >
      <Box className={CHIP_CLASS}>
        <Text variant="caption" className="text-content-primary">
          {chipLabel(position, positionExtraCount)}
        </Text>
      </Box>
    </Pressable>
  );

PositionBadgeView.displayName = "PositionBadgeView";

export default memo(PositionBadgeView);
